/**
 * AdaptiveLayout — Three-breakpoint responsive shell.
 *
 * Mobile (<768px):    TopBar + MainCanvas + RoleBottomNav
 * Tablet (768-1279px): TopBar + SidebarNav + MainCanvas
 * Desktop (≥1280px):  TopBar + SidebarNav + MainCanvas + PersistentChatPanel
 *
 * All views are driven by profile.top_level_tabs.
 * Chat on desktop is a persistent right panel, NOT a main-canvas tab.
 *
 * See: docs/architecture/UX_NAVIGATION_PATTERNS.md (v3) §2
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import TopBar from './TopBar';
import SidebarNav from './SidebarNav';
import RoleBottomNav from './RoleBottomNav';
import MainCanvas from './MainCanvas';
import PersistentChatPanel from './PersistentChatPanel';
import MobileQuickActions from '../views/MobileQuickActions';
import { saveLastView, getLastView, saveChatState } from '../utils/viewMemory';
import { isAlcedoConsoleDomain, PLATFORM_ONLY_TABS } from '../utils/domain';
import { useThreads } from '../hooks/useThreads';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { useNavigationProfile } from '../context/NavigationContext';
import { useQuickCreate } from '../hooks/useQuickCreate';

export default function AdaptiveLayout({
  // Enterprise context
  enterprise,
  betrieb,
  onOpenEnterpriseSwitcher,
  // Hamburger / menu
  onHamburger,
  // Top bar
  appName,
  unreadCount,
  onBellClick,
  onProfileClick,
  onSearch,
  // Chat (messages + handlers — shared between ChatPage and PersistentChatPanel)
  messages,
  sending,
  onSend,
  onSendFile,
  onNewSession,
  onMenuOpen,
  onShowDetail,
  seasonalContext,
  token,
  // Farm / dashboard
  dashboard,
  user,
  onUserUpdate,
  onCardTap,
  // Map
  openDetail,
  // Detail panel
  detailType,
  detailParams,
  sprayWindows,
  // A15-N7: wallet
  onOpenWallet,
  // Nav-1: phase gate
  isFeatureEnabled,
  // Account menu wrapper for TopBar avatar
  AvatarWrapper,
}) {
  const { profile } = useNavigationProfile();
  const { bp, isMobile, isTablet, isDesktop } = useBreakpoint();

  // Thread management for the persistent chat panel (desktop only)
  const threadStore = useThreads(isDesktop ? token : null);

  // UX-P2: Quick-create hook (shared between mobile FAB + desktop "+Neu")
  const quickCreate = useQuickCreate();

  // Derive available tabs from profile
  // On desktop: "chat" tab is NOT shown in nav (it's the persistent panel)
  // Defense-in-depth: strip platform-only tabs on customer domain
  const allTabs = (profile?.top_level_tabs || ['map', 'farm', 'chat']).filter(
    (t) => isAlcedoConsoleDomain() || !PLATFORM_ONLY_TABS.has(t)
  );
  const navTabs = isDesktop
    ? allTabs.filter((t) => t !== 'chat')
    : allTabs;

  // Active tab — restored from localStorage or profile default
  const [activeTab, setActiveTab] = useState(() => {
    const mem = getLastView();
    const defaultView = profile?.default_view || 'last_visited';

    if (defaultView === 'last_visited' && mem.lastView && allTabs.includes(mem.lastView)) {
      return mem.lastView;
    }
    // For specific default views
    if (defaultView !== 'last_visited' && allTabs.includes(defaultView)) {
      return defaultView;
    }
    // Fall back to first non-chat tab (or first tab)
    return navTabs[0] || allTabs[0] || 'map';
  });

  // Chat panel state (desktop) — chat_default_state from profile overrides if no localStorage memory
  const chatMem = getLastView();
  const chatDefaultCollapsed = profile?.chat_default_state === 'collapsed';
  const [chatPinned, setChatPinned] = useState(chatMem.chatPinned || false);
  const [chatCollapsed, setChatCollapsed] = useState(chatMem.chatCollapsed ?? chatDefaultCollapsed);

  // G.10: pending fly-to target — consumed by MapView after lazy mount
  const [pendingFlyToFieldId, setPendingFlyToFieldId] = useState(null);

  // Chat context for the persistent panel — updated whenever the user navigates
  const [currentContext, setCurrentContext] = useState({ view: activeTab });

  // Handle tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    saveLastView(tab);
    setCurrentContext(prev => ({ ...prev, view: tab, fieldId: null, fieldName: null, taskId: null }));
  }, []);

  // UX-P2: Quick-create handler (shared mobile FAB + desktop "+Neu")
  const handleQuickCreate = useCallback((type) => {
    quickCreate.openAction(type);
    if (type === 'note') {
      onCardTap?.('brain-dump');
      quickCreate.closeAction();
    } else if (type === 'task') {
      openDetail?.('weekly', {});
      quickCreate.closeAction();
    } else if (type === 'scouting') {
      quickCreate.createScoutingSession().then((session) => {
        if (session?.id) {
          openDetail?.('fields', { tab: 'scouting' });
        }
        quickCreate.closeAction();
      });
    } else if (type === 'poi') {
      handleTabChange('map');
      quickCreate.closeAction();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickCreate.openAction, quickCreate.closeAction, quickCreate.createScoutingSession, onCardTap, openDetail, handleTabChange]);

  // Called by MapView/DetailPage when user opens a field
  const handleFieldContext = useCallback((fieldId, fieldName) => {
    setCurrentContext(prev => ({ ...prev, fieldId, fieldName }));
    // Auto-switch desktop chat panel to the field thread (T1c)
    if (isDesktop && fieldId && !threadStore.threads.find(t => t.id === threadStore.activeThreadId)?.pinned) {
      threadStore.ensureThread('field', fieldId, fieldName).then(t => {
        if (t) threadStore.switchToThread(t.id);
      });
    }
  // Intentional: threadStore is an object whose identity changes each render; listing individual
  // stable methods avoids infinite re-render loops while still capturing the needed functionality.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, threadStore.ensureThread, threadStore.switchToThread, threadStore.threads, threadStore.activeThreadId]);

  // Called from MapContextMenu "Aufgabe erstellen" — opens WeeklyPlan in detail panel with field pre-selected
  const handleCreateTaskFromField = useCallback((fieldId, fieldName) => {
    handleFieldContext(fieldId, fieldName);
    openDetail?.('weekly', { fieldId, fieldName });
  }, [openDetail, handleFieldContext]);

  // G.10: Field Detail → Map navigation (must be declared before consumers)
  const handleShowFieldOnMap = useCallback((field) => {
    handleTabChange('map');
    openDetail?.(null); // close detail overlay
    if (field?.id && field.latitude != null && field.longitude != null) {
      setPendingFlyToFieldId(field.id);
    }
  }, [handleTabChange, openDetail]);

  // Called from MapContextMenu "Maßnahme dokumentieren" — opens FieldsDetail activities tab
  const handleCreateActivityFromField = useCallback((fieldId, fieldName) => {
    handleFieldContext(fieldId, fieldName);
    openDetail?.('fields', { fieldId, tab: 'activities', onOpenMap: handleShowFieldOnMap });
  }, [openDetail, handleFieldContext, handleShowFieldOnMap]);

  // On desktop, if "chat" is in profile tabs but hidden from nav,
  // ensure we don't land on "chat" as main canvas tab
  useEffect(() => {
    if (isDesktop && activeTab === 'chat') {
      const fallback = navTabs[0] || 'map';
      setActiveTab(fallback);
    }
  }, [isDesktop, navTabs, activeTab]);

  // Map field open → open detail overlay (no tab switch — overlay appears above map)
  const handleOpenFieldDetail = useCallback((fieldId, fieldName) => {
    openDetail?.('fields', { fieldId, onOpenMap: handleShowFieldOnMap });
    handleFieldContext(fieldId, fieldName);
  }, [openDetail, handleFieldContext, handleShowFieldOnMap]);

  // BUG 7: auto-create chat thread when a new field is created on the map
  const handleFieldCreated = useCallback((fieldId, fieldName) => {
    if (!fieldId) return;
    threadStore.ensureThread('field', fieldId, fieldName).then(t => {
      if (t) threadStore.switchToThread(t.id);
    });
  // Intentional: same threadStore identity issue as handleFieldContext above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadStore.ensureThread, threadStore.switchToThread]);

  // Card tap (dashboard kachel) — openDetail called via onCardTap (handleShowDetail in App.jsx)
  const handleCardTap = useCallback((type, params) => {
    onCardTap?.(type, params);
  }, [onCardTap]);

  // Show detail
  const handleShowDetail = useCallback((intent, params) => {
    onShowDetail?.(intent, params);
    openDetail?.(intent, params);
  }, [onShowDetail, openDetail]);

  const chatPanelVisible = isDesktop && (profile?.show_chat !== false);

  // Keyboard shortcuts (desktop only) — UX_NAVIGATION_PATTERNS.md (v3) §14
  const chatToggleRef = useRef(null);
  useEffect(() => {
    if (!isDesktop) return;
    const handler = (e) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;

      if (e.key === '1') { e.preventDefault(); if (navTabs[0]) handleTabChange(navTabs[0]); }
      else if (e.key === '2') { e.preventDefault(); if (navTabs[1]) handleTabChange(navTabs[1]); }
      else if (e.key === '3') { e.preventDefault(); if (navTabs[2]) handleTabChange(navTabs[2]); }
      else if (e.key === '/') {
        e.preventDefault();
        // Toggle chat panel collapsed state
        setChatCollapsed(c => {
          const next = !c;
          saveChatState(chatPinned, next);
          return next;
        });
      }
      // Cmd+K handled globally by useCommandPalette hook (A3-3)
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [isDesktop, navTabs, handleTabChange, chatPinned]);

  // layout_density: apply CSS class for compact/comfortable/spacious spacing
  const layoutDensity = profile?.layout_density || 'comfortable';

  return (
    <div className={`flex flex-col h-full bg-[var(--aif-bg-page)] aif-density-${layoutDensity}`} data-testid="adaptive-layout" data-bp={bp}>
      {/* ── Top Bar ─────────────────────────────────────────────────────── */}
      <TopBar
        profile={profile}
        enterprise={enterprise}
        betrieb={betrieb}
        onOpenEnterpriseSwitcher={onOpenEnterpriseSwitcher}
        onHamburger={onHamburger}
        unreadCount={unreadCount}
        onBellClick={onBellClick}
        onProfileClick={onProfileClick}
        onOpenWallet={onOpenWallet}
        onQuickCreate={handleQuickCreate}
        onSearch={onSearch}
        appName={appName}
        AvatarWrapper={AvatarWrapper}
      />

      {/* ── Body (sidebar + canvas + optional chat panel) ───────────────── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar (Tablet + Desktop only) */}
        {!isMobile && (
          <SidebarNav
            tabs={navTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            onMore={onHamburger}
            appName={appName}
          />
        )}

        {/* Main Canvas */}
        <MainCanvas
          activeTab={activeTab}
          token={token}
          dashboard={dashboard}
          onCardTap={handleCardTap}
          user={user}
          onUserUpdate={onUserUpdate}
          messages={messages}
          sending={sending}
          onSend={onSend}
          onSendFile={onSendFile}
          onNewSession={onNewSession}
          onMenuOpen={onMenuOpen}
          onShowDetail={handleShowDetail}
          appName={appName}
          unreadCount={unreadCount}
          onBellClick={onBellClick}
          seasonalContext={seasonalContext}
          onOpenFieldDetail={handleOpenFieldDetail}
          onCreateTask={handleCreateTaskFromField}
          onCreateActivity={handleCreateActivityFromField}
          onFieldCreated={handleFieldCreated}
          pendingFlyToFieldId={pendingFlyToFieldId}
          onFlyToConsumed={() => setPendingFlyToFieldId(null)}
          detailType={detailType}
          detailParams={detailParams}
          sprayWindows={sprayWindows}
          goToChat={() => { if (isMobile) handleTabChange('chat'); }}
          isDesktop={isDesktop}
          isFeatureEnabled={isFeatureEnabled}
          onViewChange={handleTabChange}
        />

        {/* Persistent Chat Panel (Desktop only) */}
        {chatPanelVisible && (
          <PersistentChatPanel
            // Thread mode (T1c) — passes thread state to panel
            threads={threadStore.threads}
            activeThreadId={threadStore.activeThreadId}
            activeMessages={threadStore.activeMessages}
            sendingThread={threadStore.sending}
            onSendInThread={(text) => threadStore.sendInThread(text, {
              view: currentContext?.view,
              field_id: currentContext?.fieldId,
              field_name: currentContext?.fieldName,
            })}
            onSwitchThread={threadStore.switchToThread}
            onPinThread={threadStore.setPinned}
            onArchiveThread={threadStore.archiveThread}
            archiveToast={threadStore.toastMessage}
            // Legacy fallback (used if threads not available)
            messages={messages}
            sending={sending}
            onSend={(text) => onSend(text, currentContext)}
            token={token}
            currentContext={currentContext}
            initialPinned={chatPinned}
            initialCollapsed={chatCollapsed}
          />
        )}
      </div>

      {/* Bottom Nav (Mobile only) — uses mobile_bottom_tabs if available, else top_level_tabs */}
      {isMobile && (
        <div style={{ paddingBottom: 56 /* reserve space for fixed bottom nav */ }}>
          <RoleBottomNav
            tabs={profile?.mobile_bottom_tabs || navTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
          {/* UX-P2: Mobile FAB speed-dial — same 4 actions as desktop "+Neu" */}
          <MobileQuickActions onAction={handleQuickCreate} />
        </div>
      )}
    </div>
  );
}
